import { useReveal } from '@/hooks/useReveal';

const comparisons = [
  { aspect: 'Response Time', without: 'Hours or days — customers leave', with: 'Under 2 minutes — 24/7 AI response', icon: 'ri-timer-line' },
  { aspect: 'Lead Capture', without: 'Manual entry, leads slip through cracks', with: 'Auto-capture from all channels into CRM', icon: 'ri-user-add-line' },
  { aspect: 'Follow-ups', without: 'Forgotten, inconsistent, exhausting', with: 'Automated drip sequences, zero effort', icon: 'ri-loop-left-line' },
  { aspect: 'Customer Data', without: 'Scattered across phones and notebooks', with: 'One unified CRM with full history', icon: 'ri-database-2-line' },
  { aspect: 'Team Collaboration', without: 'No visibility, duplicated efforts', with: 'Shared inbox, smart routing, internal notes', icon: 'ri-team-line' },
  { aspect: 'Marketing Campaigns', without: 'SMS/email with 5-20% open rates', with: 'WhatsApp with 98% open rates, rich media', icon: 'ri-megaphone-line' },
  { aspect: 'Appointment Management', without: '15-20% no-show rate, manual calls', with: 'Automated booking + reminders, 70% fewer no-shows', icon: 'ri-calendar-check-line' },
  { aspect: 'Payment Collection', without: 'Chasing payments, awkward follow-ups', with: 'Auto-invoice + payment links on WhatsApp', icon: 'ri-bank-card-line' },
  { aspect: 'Reporting', without: 'No data — gut feelings and guesswork', with: 'Real-time dashboards with 50+ metrics', icon: 'ri-bar-chart-box-line' },
  { aspect: 'Scalability', without: 'Hire more people = more cost', with: 'AI scales infinitely at near-zero marginal cost', icon: 'ri-rocket-line' },
];

export default function WhyDigiFlow() {
  const { ref, visible } = useReveal();

  return (
    <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold uppercase tracking-wider mb-5">
            The Difference
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Without DGFlow vs.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              With DGFlow
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Most businesses run WhatsApp the hard way. See the difference automation makes across every dimension of your business.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="max-w-5xl mx-auto">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-4 border-b border-background-200 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
            <div className="col-span-3">Aspect</div>
            <div className="col-span-4">Without DGFlow</div>
            <div className="col-span-1"></div>
            <div className="col-span-4">With DGFlow</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-background-200/70">
            {comparisons.map(row => (
              <div key={row.aspect} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 py-5 items-center hover:bg-background-100/50 transition-colors rounded-xl">
                {/* Aspect */}
                <div className="md:col-span-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${row.icon} text-sm text-foreground-500`}></i>
                  </div>
                  <span className="text-sm font-bold font-heading text-foreground-900">{row.aspect}</span>
                </div>

                {/* Without */}
                <div className="md:col-span-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-xs text-rose-500"></i>
                  </span>
                  <span className="text-sm text-foreground-600">{row.without}</span>
                </div>

                {/* Arrow */}
                <div className="md:col-span-1 flex justify-center">
                  <i className="ri-arrow-right-line text-primary-400 text-xl hidden md:block"></i>
                  <i className="ri-arrow-down-line text-primary-400 md:hidden"></i>
                </div>

                {/* With */}
                <div className="md:col-span-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xs text-primary-600"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-900">{row.with}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}